<script setup>
import { useMapGetter } from 'dashboard/composables/store.js';
import Icon from 'next/icon/Icon.vue';
import SidebarUnreadBadge from './SidebarUnreadBadge.vue';

const props = defineProps({
  to: { type: [Object, String], default: '' },
  label: { type: String, default: '' },
  icon: { type: [String, Object], default: '' },
  isActive: { type: Boolean, default: false },
  hasActiveChild: { type: Boolean, default: false },
  getterKeys: { type: Object, default: () => ({}) },
  // A group whose header count is a backlog rather than an unread tally names
  // it here; see SidebarUnreadBadge.
  badgeCountLabelKey: { type: String, default: undefined },
  // Groups with children fold text.com-style: the chevron collapses the
  // group's links while the label keeps navigating.
  foldable: { type: Boolean, default: false },
  folded: { type: Boolean, default: false },
});

const emit = defineEmits(['select', 'toggleFold']);

const showBadge = useMapGetter(props.getterKeys.badge);
const dynamicCount = useMapGetter(props.getterKeys.count);
</script>

<template>
  <component
    :is="to ? 'router-link' : 'div'"
    class="flex items-center gap-2 px-2 rounded-lg h-9 min-w-0 transition-colors"
    role="button"
    draggable="false"
    :to="to"
    :title="label"
    :class="{
      'bg-n-alpha-2 text-n-slate-12': isActive && !hasActiveChild,
      'text-n-slate-11': hasActiveChild,
      'text-n-slate-10 hover:bg-n-alpha-2 hover:text-n-slate-11':
        !isActive && !hasActiveChild,
    }"
    @click.stop="emit('select')"
  >
    <div v-if="icon" class="relative flex items-center gap-2">
      <Icon v-if="icon" :icon="icon" class="size-4" />
      <span
        v-if="showBadge"
        class="size-2 -top-px ltr:-right-px rtl:-left-px bg-n-brand absolute rounded-full border border-n-solid-2"
      />
    </div>
    <div
      class="flex items-center gap-1.5 flex-grow justify-between min-w-0 flex-1"
    >
      <span class="truncate text-xs font-medium uppercase tracking-wide">
        {{ label }}
      </span>
      <button
        v-if="foldable"
        type="button"
        class="grid size-5 flex-shrink-0 place-items-center rounded text-n-slate-10 hover:bg-n-alpha-2 hover:text-n-slate-12"
        :aria-expanded="!folded"
        :aria-label="label"
        @click.prevent.stop="emit('toggleFold')"
      >
        <span
          class="i-ph-caret-down size-3.5 transition-transform"
          :class="{ 'ltr:-rotate-90 rtl:rotate-90': folded }"
        />
      </button>
      <SidebarUnreadBadge
        :count="dynamicCount"
        :label-key="badgeCountLabelKey"
      />
    </div>
  </component>
</template>
